import { type Market } from "@dotcom/markets";
import {
  useMediaQuery,
  VideoPlayer,
} from "@polestar/component-warehouse-react";

import getDeviceAsset from "./getDeviceAsset";

import { Section } from "~/common/components";
import { type Video as VideoType } from "~/common/types";

interface Props {
  readonly videoMobile?: VideoType;
  readonly videoTablet?: VideoType;
  readonly videoDesktop?: VideoType;
  readonly captionsUrl?: string;
  readonly posterUrlMobile?: string;
  readonly posterUrlTablet?: string;
  readonly posterUrlDesktop?: string;
  readonly sectionSlug?: string;
  readonly market: Market;
}

const imageWidthDesktop = 1250;
const imageWidthMobile = 768;

const NewsVideoSection = ({
  videoMobile,
  videoTablet,
  videoDesktop,
  captionsUrl,
  posterUrlMobile,
  posterUrlTablet,
  posterUrlDesktop,
  sectionSlug,
  market,
}: Props) => {
  const { queryMatches } = useMediaQuery();

  const video = getDeviceAsset<VideoType>({
    isMobile: queryMatches.mobile,
    isTablet: queryMatches.tablet,
    isDesktop: queryMatches.desktopWide || queryMatches.desktop,
    assetMobile: videoMobile,
    assetTablet: videoTablet,
    assetDesktop: videoDesktop,
    assetDefault: undefined,
  });

  const posterUrl = getDeviceAsset<string | undefined>({
    isMobile: queryMatches.mobile,
    isTablet: queryMatches.tablet,
    isDesktop: queryMatches.desktopWide || queryMatches.desktop,
    assetMobile: posterUrlMobile
      ? `${posterUrlMobile}?w=${imageWidthMobile}`
      : undefined,
    assetTablet: posterUrlTablet
      ? `${posterUrlTablet}?w=${imageWidthDesktop}`
      : undefined,
    assetDesktop: posterUrlDesktop
      ? `${posterUrlDesktop}?w=${imageWidthDesktop}`
      : undefined,
    assetDefault: undefined,
  });

  return (
    <Section media id={sectionSlug}>
      <VideoPlayer
        controls
        data-track="video"
        data-testid="video"
        poster={posterUrl}
        crossOrigin="anonymous"
      >
        {video && (
          <source key={video.id} src={video.url} type={video.mimeType} />
        )}
        {captionsUrl && (
          <track
            default
            label={market.languageName}
            kind="subtitles"
            data-testid="subtitles"
            src={captionsUrl}
            srcLang={market.languageCode}
          />
        )}
      </VideoPlayer>
    </Section>
  );
};

export default NewsVideoSection;
